import { C, PageHeader, GlassCard, Badge, Skeleton, Stat } from "@coremonit/design-system";
import { Shield, ShieldCheck, Key, Users2, Network, PackageOpen, BugPlay, FileLock2 } from "lucide-react";
import { useCertificates, useNetworkPolicies, useSBOM, useSecrets, useSecuritySummary, useVulnerabilities } from "../api/hooks";

export function SecurityPage() {
  const sum = useSecuritySummary();
  const certs = useCertificates();
  const secrets = useSecrets();
  const netpols = useNetworkPolicies();
  const vulns = useVulnerabilities();
  const sbom = useSBOM();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader icon={<Shield size={16} />} title="Security" subtitle="mTLS · certificates · secrets · RBAC · network policies · vulnerability scan · SBOM" />

      {sum.isLoading ? <Skeleton height={140} /> : sum.data && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
          <Stat label="mTLS coverage"      value={`${(sum.data.mTLS as any).coveragePct}%`} color={C.green} icon={<ShieldCheck size={11} />} sublabel="Service-to-service" />
          <Stat label="Certificates"       value={(sum.data.certificates as any).total}     color={C.blue}  icon={<FileLock2 size={11} />} sublabel={`${(sum.data.certificates as any).expiring} expiring · ${(sum.data.certificates as any).expired} expired`} />
          <Stat label="Vulns (critical)"   value={(sum.data.vulns as any).critical}          color={C.red}   icon={<BugPlay size={11} />} sublabel={`${(sum.data.vulns as any).high} high · ${(sum.data.vulns as any).medium} medium`} />
          <Stat label="Network policies"   value={(sum.data.networkPolicies as any).total}   color={C.cyan}  icon={<Network size={11} />} sublabel={`${(sum.data.networkPolicies as any).enforced} enforced`} />
          <Stat label="Supply chain"       value={(sum.data.supplyChain as any).signed}      color={C.purple} icon={<PackageOpen size={11} />} sublabel={`${(sum.data.supplyChain as any).unsigned} unsigned · ${(sum.data.supplyChain as any).provenanceAttested} attested`} />
          <Stat label="RBAC roles"         value={(sum.data.rbac as any).roles.length}       color={C.blue}  icon={<Users2 size={11} />} sublabel={`${(sum.data.rbac as any).bindings} bindings`} />
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <GlassCard style={{ padding: 14 }}>
          <SectionTitle title="Certificates" />
          {certs.isLoading ? <Skeleton height={200} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 300, overflow: "auto" }}>
              {certs.data?.items.map((c) => (
                <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                  <div>
                    <div style={{ color: C.text, fontSize: 11, fontWeight: 600, fontFamily: "ui-monospace, monospace" }}>{c.subject}</div>
                    <div style={{ color: C.textMuted, fontSize: 9 }}>expires {new Date(c.expiresAt).toLocaleDateString()}</div>
                  </div>
                  <Badge color={c.status === "expired" ? "red" : c.status === "expiring" ? "amber" : "green"}>{c.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
        <GlassCard style={{ padding: 14 }}>
          <SectionTitle title="Vulnerabilities" />
          {vulns.isLoading ? <Skeleton height={200} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 300, overflow: "auto" }}>
              {vulns.data?.items.slice(0, 30).map((v) => (
                <div key={v.id} style={{ display: "grid", gridTemplateColumns: "70px 1fr auto", gap: 8, alignItems: "center", padding: "6px 10px", background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                  <Badge color={v.severity === "critical" ? "red" : v.severity === "high" ? "amber" : "muted"}>{v.severity}</Badge>
                  <div>
                    <div style={{ color: C.text, fontSize: 11, fontFamily: "ui-monospace, monospace" }}>{v.cve}</div>
                    <div style={{ color: C.textMuted, fontSize: 9 }}>{v.component} {v.version} → fixed in {v.fixedIn ?? "n/a"}</div>
                  </div>
                  <div style={{ color: C.textMuted, fontSize: 9 }}>{v.serviceId}</div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
        <GlassCard style={{ padding: 14 }}>
          <SectionTitle title="Network policies" />
          {netpols.isLoading ? <Skeleton height={160} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 260, overflow: "auto" }}>
              {netpols.data?.items.map((n) => (
                <div key={n.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                  <div>
                    <div style={{ color: C.text, fontSize: 11, fontWeight: 600 }}>{n.name}</div>
                    <div style={{ color: C.textMuted, fontSize: 9 }}>ingress {n.ingress} · egress {n.egress}</div>
                  </div>
                  <Badge color={n.enforced ? "green" : "amber"}>{n.enforced ? "enforced" : "monitor"}</Badge>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
        <GlassCard style={{ padding: 14 }}>
          <SectionTitle title="SBOM (direct + indirect)" />
          {sbom.isLoading ? <Skeleton height={160} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 260, overflow: "auto" }}>
              {sbom.data?.items.map((s, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 90px 90px 60px", gap: 8, padding: "6px 10px", background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11 }}>
                  <span style={{ color: C.text }}>{s.name}</span>
                  <span style={{ color: C.textSub, fontFamily: "ui-monospace, monospace" }}>{s.version}</span>
                  <span style={{ color: C.textMuted }}>{s.license}</span>
                  <span><Badge color={s.direct ? "blue" : "muted"}>{s.direct ? "direct" : "trans"}</Badge></span>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}

function SectionTitle({ title }: { title: string }) {
  return <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>{title}</div>;
}
